import { normalize, schema } from 'normalizr';
import * as R from 'ramda';

import generateId from '../../utils/generateId';
import sportsApi from '../../utils/sportsApi';

import {
    sort,
    order,
    insightsGroupBy,
} from '../../utils/getDefaultFilterLocalStorage';
import makeValidRspsList from '../../utils/makeValidRspsList';
import transformRspsList from '../../utils/transformRspsList';
import { getSelectedNestedRightsholders } from '../../utils/filtersHelperFunctions';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import {
    applyFullLeagueSelectionRules,
    isLeagueRightsholder,
    translateRightsholderIdsByName,
    translatePlacementIdsByName,
} from '../../utils/translateAttributionIds';
import {
    PREMIUM_COLLECTIONS as COLLECTIONS_ID,
    PREMIUM_PLACEMENT_TYPES as PLACEMENT_TYPES_ID,
    LINKED_FILTER_LOGIC as FILTER_LOGIC_ID,
    INTERNATIONAL_BROADCAST_VIEW as INTERNATIONAL_BROADCAST_VIEW_ID,
    EDGE_RIGHTSHOLDER_ATTRIBUTION as EDGE_RIGHTSHOLDER_ATTRIBUTION_ID,
    REPORTS_BROADCAST_COUNTRY_FILTER as REPORTS_BROADCAST_COUNTRY_FILTER_ID,
} from '../../constants/config';

import { parseStartDate, parseEndDate } from '../../utils/parseDate';

const GROUPED_PLACEMENTS_SUFFIXES = ['- Hashtag', '- Mention', '- Keyword'];
const getFiltersPlacements = (selectedPlacements, placementsList) => {
    // groupedPlacementSuffixesForRh consists of sets of text placements suffixes
    // for which a rightsholder has any of these placements.
    // an example of the result of groupedPlacementSuffixesForRh is:
    // {
    //     '1': ['- Hashtag', '- Keyword'],
    //     '2': ['- Keyword'],
    // }
    const groupedPlacementSuffixesForRh = {};
    selectedPlacements.forEach(rpid => {
        const placement = placementsList[rpid];
        if (!placement) return;

        const rh = rpid.split('-')[0];
        GROUPED_PLACEMENTS_SUFFIXES.forEach(suffix => {
            if (placement.name.endsWith(suffix)) {
                if (!groupedPlacementSuffixesForRh[rh]) {
                    // initializing RH placement type suffixes in case of not existing.
                    groupedPlacementSuffixesForRh[rh] = [];
                }
                if (groupedPlacementSuffixesForRh[rh].indexOf(suffix) === -1) {
                    // adding suffix if not already in the list
                    groupedPlacementSuffixesForRh[rh].push(suffix);
                }
            }
        });
    });

    const regroupedPlacements = [];
    Object.values(placementsList).forEach(placement => {
        const rh = placement.id.split(',')[0];
        const isGroupedPlacement = groupedPlacementSuffixesForRh[rh]?.some(
            suffix => placement.name.endsWith(suffix),
        );
        if (
            isGroupedPlacement ||
            selectedPlacements.indexOf(placement.id) !== -1
        ) {
            // If the placement has either a suffix for the grouped RH suffixes
            // OR the placement is in the selected placements.
            regroupedPlacements.push(placement.id);
        }
    });

    return regroupedPlacements;
};

// Schemas
const listSchema = [new schema.Entity('list')];

const rhsSchema = [
    new schema.Entity('rightsholders', {
        rightsholderPlacements: [
            new schema.Entity(
                'placements',
                {},
                {
                    idAttribute(value, parent) {
                        return `${parent.id}-${value.id}`;
                    },
                    processStrategy(value, parent) {
                        return {
                            ...value,
                            id: `${parent.id}-${value.id}`,
                        };
                    },
                },
            ),
        ],
    }),
];

// Helper method
const normalizeList = arr =>
    Array.isArray(arr) ? normalize(arr, listSchema).entities.list || {} : {};

const normalizeRightsholdersAndPlacements = arr =>
    Array.isArray(arr) ? normalize(arr, rhsSchema).entities || {} : {};

const fetcher = url => sportsApi.url(url).get().json();

const fetchConditionally = (condition, fallback, url) =>
    condition ? fetcher(url) : Promise.resolve(fallback);

const filterSelectedByList = (selected = [], list = {}) =>
    selected.filter(value => Object.prototype.hasOwnProperty.call(list, value));

const buildRightsholdersLeaguePairsFromSelection = (
    selectedRightsholderIds = [],
    rightsholdersList = {},
    teamRightsholdersList = null,
) =>
    selectedRightsholderIds.reduce((acc, rhId) => {
        const rh = rightsholdersList[rhId];
        if (
            !rh ||
            isLeagueRightsholder(rh, {
                leagueModeList: rightsholdersList,
                teamModeList: teamRightsholdersList,
            })
        ) {
            return acc;
        }
        const leagueId = rh.leagueId || 'other';
        acc[`${leagueId}-${rhId}`] = true;
        return acc;
    }, {});

const getSavedAttributionMode = filterJson => {
    const mode = filterJson?.rightsholderAttributionMode;
    return mode === 'team' || mode === 'league' ? mode : null;
};

const getSelectedOrDefault = (storeSelected = [], defaultSelected = []) =>
    storeSelected.length > 0 ? storeSelected : defaultSelected;

const getStoreArrayOrDefault = (storeValue = [], defaultValue = []) =>
    storeValue.length > 0 ? storeValue : defaultValue;

const getFilters = store => async state => {
    const userClientId = state.user.selectedUserClientId;
    const clientId = state.user.selectedClientId;
    const client = state.user.clientsById[clientId];
    const filters = state.clients[userClientId].filters;

    // Sets filters as loading
    const loadingState = R.assocPath(
        ['clients', userClientId, 'filters', 'isLoading'],
        true,
        state,
    );
    store.setState(loadingState);

    const userId = state.user.id;

    const hasCollections = client.permissions.includes(COLLECTIONS_ID);

    const hasPlacementTypes = client.permissions.includes(PLACEMENT_TYPES_ID);

    const hasFilterLogic = client.permissions.includes(FILTER_LOGIC_ID);
    const hasRightsholderAttributionPermission = client.permissions.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION_ID,
    );
    const useRightsholderAttribution = getUseRightsholderAttribution(
        client,
        clientId,
    );
    const targetAttributionMode = useRightsholderAttribution
        ? 'league'
        : 'team';
    const isAttributionModeChange =
        hasRightsholderAttributionPermission &&
        filters.rightsholderAttributionMode != null &&
        filters.rightsholderAttributionMode !== targetAttributionMode;

    const hasInternationalBroadcastView = client.permissions.includes(
        INTERNATIONAL_BROADCAST_VIEW_ID,
    );
    const hasReportsBroadcastCountryFilter = client.permissions.includes(
        REPORTS_BROADCAST_COUNTRY_FILTER_ID,
    );

    let rhsAndPlacementsList;
    let sponsorsData;
    let accOwnTypesData;
    let mediaTypesData;
    let platformsData;
    let userGlobalFilters;
    let collectionsData;
    let placementTypesData;
    let rsps;
    let countriesData;
    let networksData;

    try {
        [
            rhsAndPlacementsList,
            sponsorsData,
            accOwnTypesData,
            mediaTypesData,
            platformsData,
            userGlobalFilters,
            collectionsData,
            placementTypesData,
            rsps,
            countriesData,
            networksData,
        ] = await Promise.all([
            fetcher(
                `/rightsholders?includePlacements=true&includeTextTypePlacements=true&includeSportId=true${
                    useRightsholderAttribution
                        ? '&useRightsholderAttribution=true'
                        : ''
                }`,
            ),
            fetcher('/sponsors'),
            fetcher('/social-account-ownership-types'),
            fetcher('/media-types'),
            fetcher('/social-platforms'),
            fetcher(`/users/${userId}/mvc-global-filters`).catch(() => null),
            fetchConditionally(
                hasCollections,
                [],
                `/clients/${clientId}/collections`,
            ),
            fetchConditionally(hasPlacementTypes, [], `/placement-types`),
            fetchConditionally(
                hasFilterLogic,
                [],
                `/clients/${clientId}/rsps${
                    useRightsholderAttribution
                        ? '?useRightsholderAttribution=true'
                        : ''
                }`,
            ),
            fetchConditionally(
                hasInternationalBroadcastView ||
                    hasReportsBroadcastCountryFilter,
                [],
                `/clients/${clientId}/countries`,
            ),
            fetcher(`/clients/${clientId}/networks`),
        ]);
    } catch (error) {
        const currentState = store.getState();

        if (R.path(['clients', userClientId, 'filters'], currentState)) {
            store.setState(
                R.assocPath(
                    ['clients', userClientId, 'filters', 'isLoading'],
                    false,
                    currentState,
                ),
            );
        }

        return;
    }

    // TODO: check how to handle non-loading datasets

    const sponsorsList = normalizeList(sponsorsData);
    const accOwnTypesList = normalizeList(accOwnTypesData);
    const mediaTypesList = normalizeList(mediaTypesData);
    const platformsList = normalizeList(platformsData);
    const collectionsList = normalizeList(collectionsData);

    const reducedRsps = hasFilterLogic
        ? transformRspsList(Array.isArray(rsps) ? rsps : [], hasPlacementTypes)
        : [];
    const validRsps = hasFilterLogic
        ? makeValidRspsList(reducedRsps, hasPlacementTypes)
        : [];

    const audiencePlatformList = Object.keys(platformsList).reduce(
        (acc, pId) => {
            if (R.includes(pId, ['1', '2', '3'])) {
                acc[pId] = platformsList[pId];
            }
            return acc;
        },
        {},
    );

    const countriesList = normalizeList(countriesData);

    const networksList = normalizeList(networksData);

    const {
        rightsholders: rightsholdersList = {},
        placements: placementsList = {},
    } = normalizeRightsholdersAndPlacements(rhsAndPlacementsList);

    const leagueIds = Object.entries(rightsholdersList).reduce(
        (acc, [, rightsholder]) => {
            if (!acc.includes(rightsholder.leagueId)) {
                acc.push(rightsholder.leagueId);
            }

            return acc;
        },
        [],
    );

    // hack to show only placement types compatible with the current client
    const clientPlacementTypeIds = Object.values(placementsList).reduce(
        (acc, pl) => {
            const { placementType: plType } = pl;
            if (plType && !R.includes(plType.id, acc)) {
                acc.push(plType.id);
            }
            return acc;
        },
        [],
    );

    const plcTypes = Array.isArray(placementTypesData)
        ? placementTypesData
        : [];

    const placementTypesList = normalizeList(
        plcTypes.reduce((acc, plType) => {
            if (R.includes(plType.id, clientPlacementTypeIds)) {
                acc.push(plType);
            }
            return acc;
        }, []),
    );

    const changesId = generateId();
    const rawFilterJson = R.path(['filterJson'], userGlobalFilters) || '{}';

    const filterId = R.path(['id'], userGlobalFilters) || null;

    let filterJson = JSON.parse(rawFilterJson);

    // Fix for users that have setted their default filters with old global filters
    if (
        filterJson.rightsholdersLeaguePairs &&
        filterJson.rightsholders?.length === 0
    ) {
        getSelectedNestedRightsholders(
            filterJson.rightsholdersLeaguePairs,
        ).forEach(nestedRh => {
            filterJson.rightsholders.push(nestedRh);
        });
    } else if (
        filterJson.rightsholders?.length &&
        !Object.keys(filterJson.rightsholdersLeaguePairs || {}).length
    ) {
        filterJson.rightsholdersLeaguePairs = filterJson.rightsholders.reduce(
            (acc, rh) => {
                const rightsholder = rightsholdersList[rh];

                if (!rightsholder) {
                    return acc;
                }

                const { id, leagueId } = rightsholder;
                return {
                    ...acc,
                    [`${leagueId}-${id}`]: true,
                };
            },
            {},
        );
    }

    const {
        rightsholders = [],
        sponsors = [],
        placements = [],
        placementTypes = [],
        mediaTypes = [],
        accOwnTypes = [],
        platformPostTypePairs = [],
        audiencePlatforms = [],
        accountUsernames = [],
        authorshipTypes = [],
        collaboratingAccountUsernames = [],
        hashtags = [],
        captions = [],
        collections = [],
        countries = [],
        broadcastNetworks = [],
    } = filterJson;

    const filterStartDate = R.path(['startDate'], filterJson) || undefined;
    const filterEndDate = R.path(['endDate'], filterJson) || undefined;
    const filterDateRange = R.path(['dateRange'], filterJson) || null;

    const hasDefaultFilters = !!filterId;
    const savedAttributionMode = getSavedAttributionMode(filterJson);
    const currentAttributionMode = useRightsholderAttribution
        ? 'league'
        : 'team';
    const shouldUseSavedModeFallback =
        hasRightsholderAttributionPermission &&
        hasDefaultFilters &&
        currentAttributionMode === 'league' &&
        (rightsholders.length > 0 || placements.length > 0);
    const sourceAttributionMode = shouldUseSavedModeFallback
        ? savedAttributionMode || 'team'
        : currentAttributionMode;

    let sourceRightsholdersList = filters.rightsholders?.list || {};
    let sourcePlacementsList = filters.placements?.list || {};

    if (sourceAttributionMode !== currentAttributionMode) {
        try {
            const sourceRhsAndPlacements = await fetcher(
                `/rightsholders?includePlacements=true&includeTextTypePlacements=true&includeSportId=true${
                    sourceAttributionMode === 'league'
                        ? '&useRightsholderAttribution=true'
                        : ''
                }`,
            );
            const {
                rightsholders: sourceRightsholders = {},
                placements: sourcePlacements = {},
            } = normalizeRightsholdersAndPlacements(sourceRhsAndPlacements);
            sourceRightsholdersList = sourceRightsholders;
            sourcePlacementsList = sourcePlacements;
        } catch (error) {
            sourceRightsholdersList = filters.rightsholders?.list || {};
            sourcePlacementsList = filters.placements?.list || {};
        }
    }

    let startDate = filters.startDate;
    let endDate = filters.endDate;
    let dateSpec = filters.dateSpec;
    const dateRange = isAttributionModeChange
        ? filters.dateRange ?? filterDateRange
        : filterDateRange;

    if (hasDefaultFilters && !isAttributionModeChange) {
        // The default dates replace the stored ones, so a stored spec no longer
        // describes them.
        dateSpec = null;

        if (filterDateRange) {
            const today = new Date();

            startDate = new Date(
                new Date().setDate(today.getDate() - filterDateRange + 1),
            );
            startDate.setHours(0, 0, 0, 0);
            startDate = parseStartDate(startDate);

            endDate = today;
            endDate.setHours(23, 59, 59, 999);
            endDate = parseEndDate(endDate);
        } else {
            startDate = filterStartDate;
            endDate = filterEndDate;
        }
    }

    const hasSeveralRightsholders =
        Object.entries(rightsholdersList).length > 1;
    const hasSeveralLeagues = leagueIds.length > 1;
    const shouldShowLeagueHierarchy =
        hasSeveralRightsholders && hasSeveralLeagues;

    const selectedRightsholders =
        (filters.rightsholders?.selected?.length ?? 0) > 0
            ? filters.rightsholders.selected
            : rightsholders;
    const translatedSelectedRightsholders = translateRightsholderIdsByName(
        selectedRightsholders,
        sourceRightsholdersList,
        rightsholdersList,
    );
    const sourceAttributionModeForRules =
        isAttributionModeChange && filters.rightsholderAttributionMode
            ? filters.rightsholderAttributionMode
            : shouldUseSavedModeFallback
              ? savedAttributionMode || 'team'
              : currentAttributionMode;
    const normalizedSelectedRightsholders =
        sourceAttributionModeForRules !== currentAttributionMode
            ? applyFullLeagueSelectionRules({
                  selectedRightsholderIds: translatedSelectedRightsholders,
                  preTranslationSelectedIds: selectedRightsholders,
                  sourceRightsholdersList,
                  targetRightsholdersList: rightsholdersList,
                  sourceAttributionMode: sourceAttributionModeForRules,
                  targetAttributionMode: currentAttributionMode,
              })
            : translatedSelectedRightsholders;
    const validSelectedRightsholders = filterSelectedByList(
        normalizedSelectedRightsholders,
        rightsholdersList,
    );

    const rightsholderIdMap = selectedRightsholders.reduce((acc, oldRhId) => {
        const oldRhName = sourceRightsholdersList?.[oldRhId]?.name;
        if (!oldRhName) return acc;
        const mappedRhId = validSelectedRightsholders.find(
            newRhId => rightsholdersList[newRhId]?.name === oldRhName,
        );
        if (mappedRhId) {
            acc[String(oldRhId)] = String(mappedRhId);
        }
        return acc;
    }, {});

    const selectedSponsors =
        (filters.sponsors?.selected?.length ?? 0) > 0
            ? filters.sponsors.selected
            : sponsors;
    const selectedPlacements =
        (filters.placements?.selected?.length ?? 0) > 0
            ? filters.placements.selected
            : placements;
    const translatedSelectedPlacements = translatePlacementIdsByName({
        selectedPlacementIds: selectedPlacements,
        oldPlacementsList: sourcePlacementsList,
        newPlacementsList: placementsList,
        rightsholderIdMap,
        translatedRightsholderIds: validSelectedRightsholders,
        enforceMatchedRightsholders: selectedRightsholders.length > 0,
    });
    const validSelectedPlacements = filterSelectedByList(
        translatedSelectedPlacements,
        placementsList,
    );
    const rebuiltRightsholdersLeaguePairs =
        buildRightsholdersLeaguePairsFromSelection(
            validSelectedRightsholders,
            rightsholdersList,
            currentAttributionMode === 'league' &&
                sourceAttributionModeForRules === 'team'
                ? sourceRightsholdersList
                : null,
        );
    const regroupedPlacements = getFiltersPlacements(
        validSelectedPlacements,
        placementsList,
    );

    const selectedPlacementTypes = getSelectedOrDefault(
        filters.placementTypes?.selected,
        placementTypes,
    );
    const validSelectedPlacementTypes = filterSelectedByList(
        selectedPlacementTypes,
        placementTypesList,
    );
    const selectedAccOwnTypes = getSelectedOrDefault(
        filters.accOwnTypes?.selected,
        accOwnTypes,
    );
    const validSelectedAccOwnTypes = filterSelectedByList(
        selectedAccOwnTypes,
        accOwnTypesList,
    );
    const selectedMediaTypes = getSelectedOrDefault(
        filters.mediaTypes?.selected,
        mediaTypes,
    );
    const validSelectedMediaTypes = filterSelectedByList(
        selectedMediaTypes,
        mediaTypesList,
    );
    const selectedPlatformPostTypePairs = getSelectedOrDefault(
        filters.platformPostTypePairs?.selected,
        platformPostTypePairs,
    );
    const validSelectedPlatformPostTypePairs = filterSelectedByList(
        selectedPlatformPostTypePairs,
        platformsList,
    );
    const selectedAudiencePlatforms = getSelectedOrDefault(
        filters.audiencePlatforms?.selected,
        audiencePlatforms,
    );
    const validSelectedAudiencePlatforms = filterSelectedByList(
        selectedAudiencePlatforms,
        audiencePlatformList,
    );
    const selectedCollections = getSelectedOrDefault(
        filters.collections?.selected,
        collections,
    );
    const validSelectedCollections = filterSelectedByList(
        selectedCollections,
        collectionsList,
    );
    const selectedCountries = getSelectedOrDefault(
        filters.countries?.selected,
        countries,
    );
    const validSelectedCountries = filterSelectedByList(
        selectedCountries,
        countriesList,
    );
    const selectedBroadcastNetworks = getSelectedOrDefault(
        filters.broadcastNetworks?.selected,
        broadcastNetworks,
    );
    const validSelectedBroadcastNetworks = filterSelectedByList(
        selectedBroadcastNetworks,
        networksList,
    );
    const selectedAccountUsernames = getStoreArrayOrDefault(
        filters.accountUsernames,
        accountUsernames,
    );
    const selectedHashtags = getStoreArrayOrDefault(filters.hashtags, hashtags);
    const selectedCaptions = getStoreArrayOrDefault(filters.captions, captions);
    const selectedAuthorshipTypes = getStoreArrayOrDefault(
        filters.authorshipTypes,
        authorshipTypes,
    );
    const selectedCollaboratingAccountUsernames = getStoreArrayOrDefault(
        filters.collaboratingAccountUsernames,
        collaboratingAccountUsernames,
    );
    const selectedInsightsGroupBy = filters.insightsGroupBy?.length
        ? filters.insightsGroupBy
        : insightsGroupBy;
    const selectedSort = filters.sort || sort;
    const selectedOrder = filters.order || order;

    store.setState(
        R.evolve(
            {
                clients: {
                    [userClientId]: {
                        filters: {
                            userDefaultFilters: {
                                id: R.always(filterId),
                                filterJson: R.always(filterJson),
                            },
                            isLoading: R.F,
                            startDate: R.always(startDate),
                            endDate: R.always(endDate),
                            dateSpec: R.always(dateSpec),
                            dateRange,
                            rightsholderAttributionMode: R.always(
                                useRightsholderAttribution ? 'league' : 'team',
                            ),
                            changesId: R.always(changesId),
                            hasSeveralRightsholders: R.always(
                                hasSeveralRightsholders,
                            ),
                            shouldShowLeagueHierarchy: R.always(
                                shouldShowLeagueHierarchy,
                            ),
                            rightsholders: {
                                list: R.always(rightsholdersList),
                                selected: R.always(validSelectedRightsholders),
                            },
                            rightsholdersLeaguePairs: R.always(
                                rebuiltRightsholdersLeaguePairs,
                            ),
                            sponsors: {
                                list: R.always(sponsorsList),
                                selected: R.always(selectedSponsors),
                            },
                            placements: {
                                list: R.always(placementsList),
                                selected: R.always(regroupedPlacements),
                            },
                            placementTypes: {
                                list: R.always(placementTypesList),
                                selected: R.always(validSelectedPlacementTypes),
                            },
                            accOwnTypes: {
                                list: R.always(accOwnTypesList),
                                selected: R.always(validSelectedAccOwnTypes),
                            },
                            mediaTypes: {
                                list: R.always(mediaTypesList),
                                selected: R.always(validSelectedMediaTypes),
                            },
                            platformPostTypePairs: {
                                list: R.always(platformsList),
                                selected: R.always(
                                    validSelectedPlatformPostTypePairs,
                                ),
                            },
                            audiencePlatforms: {
                                list: R.always(audiencePlatformList),
                                selected: R.always(
                                    validSelectedAudiencePlatforms,
                                ),
                            },
                            accountUsernames: R.always(
                                selectedAccountUsernames,
                            ),
                            hashtags: R.always(selectedHashtags),
                            captions: R.always(selectedCaptions),
                            authorshipTypes: R.always(selectedAuthorshipTypes),
                            collaboratingAccountUsernames: R.always(
                                selectedCollaboratingAccountUsernames,
                            ),
                            collections: {
                                list: R.always(collectionsList),
                                selected: R.always(validSelectedCollections),
                            },
                            insightsGroupBy: R.always(selectedInsightsGroupBy),
                            sort: R.always(selectedSort),
                            order: R.always(selectedOrder),
                            validRsps: R.always(validRsps),
                            countries: {
                                list: R.always(countriesList),
                                selected: R.always(validSelectedCountries),
                            },
                            broadcastNetworks: {
                                list: R.always(networksList),
                                selected: R.always(
                                    validSelectedBroadcastNetworks,
                                ),
                            },
                        },
                    },
                },
            },
            store.getState(),
        ),
        true,
    );
};

export default getFilters;
